import { Component, inject, OnInit } from '@angular/core';
import { ModelsUser } from "../../../app/api/model/models";
import { ApiUserRequest } from "../../../app/api/model/apiUserRequest";
import { ApiUserDataResponse } from "../../../app/api/model/apiUserDataResponse";
import { ApiUserListDataResponse } from "../../../app/api/model/apiUserListDataResponse";
import { UsersService } from "../../../app/api/api/users.service";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { ButtonModule } from "primeng/button";
import { TableModule } from "primeng/table";
import { PaginatorModule } from "primeng/paginator";
import { RippleModule } from "primeng/ripple";
import { ToolbarModule } from "primeng/toolbar";
import { InputTextModule } from "primeng/inputtext";
import { ConfirmationService, MessageService } from "primeng/api";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ToastModule } from "primeng/toast";
import { HrEmployeesForm } from './hr.employees.form';
import { Card } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { CompanyService } from '../../../app/api/api/company.service';
import { ModelsCompanyPosition } from '../../../app/api/model/models';
import { SelectModule } from 'primeng/select';

@Component({
  selector: 'app-hr-employees',
  template: `
    <p-toast></p-toast>
    <p-confirmDialog [style]="{width: '450px'}"></p-confirmDialog>
    <app-hr-employee-form 
      [visible]="employeeDialog" 
      [employee]="employee" 
      [positions]="availablePositions"
      [canEditRole]="canEditRole"
      (onSave)="saveEmployee($event)"
      (onCancel)="hideDialog()"></app-hr-employee-form>


    <p-card>
      @if (ENABLE_ADD) {
        <div class="flex justify-between mb-4">
          <div>
            <p-button label="Create" severity="success" icon="pi pi-plus" (click)="openNew()"/>
          </div>
        </div>
      }
      <p-table
        #dt
        [value]="employees"
        [rowHover]="true"
        dataKey="id"
        [paginator]="true"
        [rows]="10"
        [rowsPerPageOptions]="[10, 20, 50]"
        [lazy]="true"
        [totalRecords]="totalRecords"
        [loading]="loading"
        (onLazyLoad)="loadEmployees($event)"
        tableStyleClass="mb-4">
        <ng-template pTemplate="header">
          <tr class="hidden md:table-row">
            <th pSortableColumn="first_name">{{ 'First Name' | transloco }}
              <p-sortIcon field="first_name"></p-sortIcon>
            </th>
            <th pSortableColumn="last_name">{{ 'Last Name' | transloco }}
              <p-sortIcon field="last_name"></p-sortIcon>
            </th>
            <th pSortableColumn="phone">{{ 'Phone' | transloco }}
              <p-sortIcon field="phone"></p-sortIcon>
            </th>
            <th>{{ 'WA' | transloco }}</th>
            <th>
              {{ 'Position' | transloco }}
            </th>
           <th></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-employee>
          <!-- Desktop Row -->
          <tr class="hidden md:table-row">
            <td>{{ employee.first_name }}</td>
            <td>{{ employee.last_name }}</td>
            <td>{{ employee.phone }}</td>
            <td>
              @if (employee.wa_opt_in) {
                <i class="pi pi-whatsapp text-green-500 text-lg"></i>
              }
            </td>
            <td>
              <div class="flex flex-wrap gap-1">
                @for (pos of employee.positions; track pos.id) {
                  <p-tag [value]="pos.name" severity="secondary"></p-tag>
                }
              </div>
            </td>
           <td class="w-0">
             <div class="flex flex-row flex-nowrap">
               <button 
               pButton 
               pRipple 
               icon="pi pi-pencil" 
               class="p-button-rounded p-button-success mr-2"
               (click)="editEmployee(employee)">
               </button>
               @if (ENABLE_ARCHIVE) {
                 <button pButton pRipple icon="pi pi-trash" class="p-button-rounded p-button-danger"
                         (click)="deleteEmployee(employee)"></button>
               }
             </div>
           </td>
          </tr>
          <!-- Mobile Card -->
          <tr class="md:hidden">
            <td colspan="7" style="padding: 0">
              <div class="border rounded-xl p-4 shadow-sm bg-white dark:bg-zinc-900 my-2 mx-1" style="border-color: #2d8a4e;">
                <div class="flex justify-between items-start mb-3">
                  <div>
                    <div class="text-xl font-bold">{{ employee.first_name }} {{ employee.last_name }}</div>
                    <div class="text-zinc-500 flex items-center gap-2 mt-1">
                      <i class="pi pi-phone text-sm"></i>
                      {{ employee.phone }}
                      @if (employee.wa_opt_in) {
                        <i class="pi pi-whatsapp text-green-500"></i>
                      }
                    </div>
                  </div>
                  <div class="flex gap-2">
                    <button 
                      pButton 
                      pRipple 
                      icon="pi pi-pencil" 
                      class="p-button-rounded p-button-text p-button-success"
                      (click)="editEmployee(employee)">
                    </button>
                    @if (ENABLE_ARCHIVE) {
                      <button pButton pRipple icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger"
                              (click)="deleteEmployee(employee)"></button>
                    }
                  </div>
                </div>
                
                <div class="flex flex-wrap items-center gap-2">
                  <p-tag [value]="employee.role | transloco" [severity]="getRoleSeverity(employee.role)"></p-tag>
                  @if (employee.positions && employee.positions.length > 0) {
                    <span class="text-zinc-300">|</span>
                    <div class="flex flex-wrap gap-1">
                      @for (pos of employee.positions; track pos.id) {
                        <p-tag [value]="pos.name" severity="secondary" [rounded]="true"></p-tag>
                      }
                    </div>
                  }
                </div>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </p-card>
  `,
  standalone: true,
  imports: [
    ButtonModule,
    TableModule,
    PaginatorModule,
    RippleModule,
    ToolbarModule,
    InputTextModule,
    HrEmployeesForm,
    ConfirmDialogModule,
    ToastModule,
    Card,
    TagModule,
    TranslocoModule,
    SelectModule
  ],
  providers: [MessageService, ConfirmationService]
})
export class HrEmployeesIndex implements OnInit {
  private usersApi = inject(UsersService);
  private companyApi = inject(CompanyService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private transloco = inject(TranslocoService);

  readonly ENABLE_ADD = false;
  readonly ENABLE_ARCHIVE = false;

  currentUser?: ModelsUser;
  employees: ModelsUser[] = [];
  availablePositions: ModelsCompanyPosition[] = [];
  employeeDialog = false;
  employee: ModelsUser = {} as ModelsUser;
  totalRecords = 0;
  loading = false;
  private lastLazyLoadEvent?: any;

  get canEditRole(): boolean {
    return this.currentUser?.role === 'owner';
  }

  ngOnInit() {
    this.usersApi.apiUserGet().subscribe((r: ApiUserDataResponse) => {
      if (r.data) {
        this.currentUser = r.data;
        this.loadCompanyPositions();
      }
    });
  }

  loadCompanyPositions() {
    this.companyApi.apiCompanyPositionsGet().subscribe(r => {
      if (r.data) {
        this.availablePositions = r.data;
      }
    });
  }

  loadEmployees(event?: any) {
    if (event) {
      this.lastLazyLoadEvent = event;
    }
    this.loading = true;
    const page = this.lastLazyLoadEvent ? Math.floor(this.lastLazyLoadEvent.first / this.lastLazyLoadEvent.rows) + 1 : 1;
    const pageSize = this.lastLazyLoadEvent ? this.lastLazyLoadEvent.rows : 10;

    this.usersApi.apiUsersGet(
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      page,
      pageSize).subscribe({
        next: (r: ApiUserListDataResponse) => {
          if (r.data) {
            this.employees = r.data;
          }
          if (r.total !== undefined) {
            this.totalRecords = r.total;
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  openNew() {
    this.employee = {} as ModelsUser;
    this.employeeDialog = true;
  }

  hideDialog() {
    this.employeeDialog = false;
  }

  saveEmployee(employee: ModelsUser) {
    const isNew = !employee.id;
    const payload: ApiUserRequest = {
      company_id: employee.company_id || this.currentUser?.company_id || 1,
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      phone: employee.phone || '',
      role: employee.role || 'employee',
      language_id: employee.language_id || this.currentUser?.language_id || 1,
      wa_opt_in: employee.wa_opt_in || false,
      position_ids: employee.positions?.map(p => p.id!) || []
    };

    if (isNew) {
      this.usersApi.apiUsersPost(payload).subscribe({
        next: () => {
          this.loadEmployees();
          this.employeeDialog = false;
          this.messageService.add({ severity: 'success', summary: this.transloco.translate('Successful') as string, detail: this.transloco.translate('Employee Created') as string, life: 3000 });
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: this.transloco.translate('Error') as string, detail: this.transloco.translate('Failed to create employee') as string, life: 3000 });
        }
      });
    } else {
      this.usersApi.apiUsersIdPut(employee.id!, payload).subscribe({
        next: () => {
          this.loadEmployees();
          this.employeeDialog = false;
          this.messageService.add({ severity: 'success', summary: this.transloco.translate('Successful') as string, detail: this.transloco.translate('Employee Updated') as string, life: 3000 });
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: this.transloco.translate('Error') as string, detail: this.transloco.translate('Failed to update employee') as string, life: 3000 });
        }
      });
    }
  }

  deleteEmployee(employee: ModelsUser) {
    this.confirmationService.confirm({
      message: (this.transloco.translate('Are you sure you want to delete') as string) + ' ' + employee.first_name + ' ' + employee.last_name + '?',
      header: this.transloco.translate('Confirm') as string,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.usersApi.apiUsersIdDelete(employee.id!).subscribe({
          next: () => {
            this.loadEmployees();
            this.messageService.add({ severity: 'success', summary: this.transloco.translate('Successful') as string, detail: this.transloco.translate('Employee Deleted') as string, life: 3000 });
          },
          error: () => {
            this.messageService.add({ severity: 'error', summary: this.transloco.translate('Error') as string, detail: this.transloco.translate('Failed to delete employee') as string, life: 3000 });
          }
        });
      }
    });
  }

  editEmployee(employee: ModelsUser) {
    this.employee = { ...employee };
    this.employeeDialog = true;
  }

  getRoleSeverity(role: string | undefined): "success" | "info" | "warn" | "danger" | "secondary" | "contrast" | undefined {
    switch (role?.toLowerCase()) {
      case 'owner':
        return 'success';
      case 'manager':
        return 'info';
      case 'employee':
        return 'warn';
      default:
        return 'secondary';
    }
  }
}
